import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { decodeData, encodeData } from "../../../utils/encodeAndEncode";
import { getVideoDetailsById } from '../../../services/adminService';

function CourseVideo() {
    const { courseId, moduleId, contentId } = useParams();
    const decodedCourseId = decodeData(courseId);
    const decodedModuleId = decodeData(moduleId);
    const decodedContentId = decodeData(contentId);

    useEffect(() => {
        const fetchVideoDetails = async () => {
            try {
                const response = await getVideoDetailsById({
                    video_id: decodedContentId,
                    module_id: decodedModuleId
                });
                // console.log('Video Details Response:', response);
                
                if (response.success) {
                    setVideoData(response.data);
                }
            } catch (error) {
                console.error('Error fetching video details:', error);
            }
        };

        fetchVideoDetails();
    }, [decodedContentId, decodedModuleId]);

    // // console.log('decodedContentId', decodedContentId);
    const [activeTab, setActiveTab] = useState('overview');
    const [showEditModal, setShowEditModal] = useState(false);
    const [videoData, setVideoData] = useState({
        title: 'Introduction to the Course',
        description: 'This video covers the basic concepts and course overview.',
        videoUrl: 'video-url.mp4',
        thumbnail: '/src/assets/images/course/course1.png'
    });
    const [newVideo, setNewVideo] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [isUploading, setIsUploading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);

    const handleVideoRemove = () => {
        setVideoData(prev => ({
            ...prev,
            videoUrl: '',
            thumbnail: ''
        }));
    };

    const uploadVideoInChunks = async (file) => {
        const chunkSize = 5 * 1024 * 1024; // 5MB chunks
        const totalChunks = Math.ceil(file.size / chunkSize);
        let uploadedChunks = 0;

        setIsUploading(true);
        setUploadProgress(0);

        try {
            const formData = new FormData();
            formData.append('video', file);
            formData.append('title', videoData.title);
            formData.append('description', videoData.description);
            formData.append('module_id', decodedModuleId);
            formData.append('content_id', decodedContentId);

            // Upload with progress tracking
            const xhr = new XMLHttpRequest();

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable) {
                    const percentComplete = Math.round((e.loaded / e.total) * 100);
                    setUploadProgress(percentComplete);
                    if (percentComplete >= 90) {
                        setIsProcessing(true);
                    }
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    toast.success('Video uploaded successfully!', {
                        position: 'top-center',
                        autoClose: 3000
                    });
                    setIsUploading(false);
                    setIsProcessing(false);
                    setUploadProgress(0);
                    setShowEditModal(false);
                } else {
                    throw new Error('Upload failed');
                }
            });

            xhr.addEventListener('error', () => {
                toast.error('Upload failed. Please try again.', {
                    position: 'top-center',
                    autoClose: 3000
                });
                setIsUploading(false);
                setIsProcessing(false);
                setUploadProgress(0);
            });

            xhr.open('POST', '/api/admin/upload-video'); // Replace with your API endpoint
            xhr.send(formData);

        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed. Please try again.', {
                position: 'top-center',
                autoClose: 3000
            });
            setIsUploading(false);
            setIsProcessing(false);
            setUploadProgress(0);
        }
    };

    const handleVideoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('video/')) {
                toast.error('Please upload a valid video file', {
                    position: 'top-center',
                    autoClose: 3000
                });
                return;
            }

            // Validate file size (150MB limit)
            if (file.size > 150 * 1024 * 1024) {
                toast.error('File size should not exceed 150MB', {
                    position: 'top-center',
                    autoClose: 3000
                });
                return;
            }

            setNewVideo(file);
            setVideoData(prev => ({
                ...prev,
                videoUrl: URL.createObjectURL(file)
            }));
        }
    };

    const handleSaveChanges = () => {
        if (newVideo) {
            uploadVideoInChunks(newVideo);
        } else {
            setShowEditModal(false);
        }
    };

    return (
        <>
            <div className="flex flex-col">
                <div className="w-full mb-3 flex justify-between items-center">
                    <h4 className="mb-0 text-lg font-semibold">Course Video</h4>
                    <button 
                        className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-3.5 rounded-lg text-sm whitespace-nowrap"
                        onClick={() => setShowEditModal(true)}
                    >
                        Edit Video
                    </button>
                </div>


                {/* Video Player */}
                <div className="w-full">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="p-0">
                            <div style={{ maxWidth: '720px', margin: '0 auto' }}>
                                <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                    <video 
                                        controls 
                                        className="absolute top-0 left-0 w-full h-full rounded-t-xl"
                                        poster={videoData.thumbnail}
                                    >
                                        <source src={videoData.videoUrl} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="w-full mt-3">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100">
                        <div className="p-0">
                            {/* Tabs Navigation */}
                            <div className="border-b border-gray-200 mb-0">
                                <ul className="flex border-b border-gray-200 px-3">
                                    <li className="mr-1">
                                        <button
                                            className={`px-3 py-2 text-sm border-b-2 ${activeTab === 'overview' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                            onClick={() => setActiveTab('overview')}
                                        >
                                            Overview
                                        </button>
                                    </li>
                                    <li className="mr-1">
                                        <button
                                            className={`px-3 py-2 text-sm border-b-2 ${activeTab === 'notes' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                            onClick={() => setActiveTab('notes')}
                                        >
                                            Notes
                                        </button>
                                    </li>
                                    <li className="mr-1">
                                        <button
                                            className={`px-3 py-2 text-sm border-b-2 ${activeTab === 'comments' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                            onClick={() => setActiveTab('comments')}
                                        >
                                            Comments
                                        </button>
                                    </li>
                                    <li className="mr-1">
                                        <button
                                            className={`px-3 py-2 text-sm border-b-2 ${activeTab === 'ratings' ? 'border-primary text-primary font-medium' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
                                            onClick={() => setActiveTab('ratings')}
                                        >
                                            Ratings
                                        </button>
                                    </li>
                                </ul>
                            </div>

                            {/* Tab Content */}
                            <div className="mt-0">
                                <div className={`p-3 ${activeTab === 'overview' ? 'block' : 'hidden'}`}>
                                    <div className="overview-content space-y-3">
                                        <div>
                                            <h6 className="text-gray-500 text-xs font-medium mb-1">Duration</h6>
                                            <p className="mb-0 text-sm">{videoData.duration || '0:00'}</p>
                                        </div>
                                        <div>
                                            <h6 className="text-gray-500 text-xs font-medium mb-1">Title</h6>
                                            <h5 className="mb-0 text-sm font-semibold">{videoData.title}</h5>
                                        </div>
                                        <div>
                                            <h6 className="text-gray-500 text-xs font-medium mb-1">Description</h6>
                                            <p className="mb-0 text-sm text-gray-700">{videoData.description}</p>
                                        </div>
                                    </div>
                                </div>

                                <div className={`p-3 ${activeTab === 'notes' ? 'block' : 'hidden'}`}>
                                    <h5 className="text-sm font-semibold">Notes Section</h5>
                                </div>

                                <div className={`p-3 ${activeTab === 'comments' ? 'block' : 'hidden'}`}>
                                    <h5 className="text-sm font-semibold">Comments Section</h5>
                                </div>

                                <div className={`p-3 ${activeTab === 'ratings' ? 'block' : 'hidden'}`}>
                                    <h5 className="text-sm font-semibold">Ratings Section</h5>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Edit Video Modal */}
            {showEditModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                        <div className="px-5 py-3.5 border-b border-gray-100 flex justify-between items-center">
                            <h5 className="text-base font-semibold">Edit Video</h5>
                            <button 
                                type="button" 
                                className="text-gray-400 hover:text-gray-600 w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100"
                                onClick={() => setShowEditModal(false)}
                            >
                                <Icon icon="mdi:close" width="20" height="20" />
                            </button>
                        </div>
                        <div className="p-5">
                            {/* Title and Description Fields */}
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-font-primary mb-1.5">Video Title</label>
                                <input
                                    type="text"
                                    className="w-full px-3 py-2 text-sm border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    value={videoData.title}
                                    onChange={(e) => setVideoData(prev => ({
                                        ...prev,
                                        title: e.target.value
                                    }))}
                                />
                            </div>
                            <div className="mb-3">
                                <label className="block text-sm font-medium text-font-primary mb-1.5">Video Description</label>
                                <textarea
                                    className="w-full px-3 py-2 text-sm border border-input-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
                                    rows="3"
                                    value={videoData.description}
                                    onChange={(e) => setVideoData(prev => ({
                                        ...prev,
                                        description: e.target.value
                                    }))}
                                ></textarea>
                            </div>

                            {/* Current Video Preview */}
                            {videoData.videoUrl ? (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-3">
                                    <div className="p-3">
                                        <div className="flex justify-between items-center mb-2">
                                            <span className="text-xs font-medium text-gray-500">Current Video</span>
                                            <button 
                                                className="inline-flex items-center gap-1.5 border border-red-500 text-red-500 hover:bg-red-50 font-medium py-1.5 px-3 rounded-lg text-xs"
                                                onClick={handleVideoRemove}
                                            >
                                                <Icon icon="fluent:delete-24-regular" width="14" height="14" />
                                                Remove
                                            </button>
                                        </div>
                                        <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                                            <video 
                                                controls 
                                                className="absolute top-0 left-0 w-full h-full rounded-lg"
                                                poster={videoData.thumbnail}
                                            >
                                                <source src={videoData.videoUrl} type="video/mp4" />
                                            </video>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-100 mb-3">
                                    <div className="p-4">
                                        <div className="text-center p-4">
                                            <input
                                                type="file"
                                                id="videoUpload"
                                                className="hidden"
                                                accept="video/*"
                                                onChange={handleVideoUpload}
                                            />
                                            <label 
                                                htmlFor="videoUpload" 
                                                className="border border-primary text-primary hover:bg-primary/10 font-medium py-2 px-4 rounded-lg flex flex-col items-center gap-2 mx-auto cursor-pointer text-sm"
                                                style={{ width: 'fit-content' }}
                                            >
                                                <Icon icon="fluent:video-add-24-regular" width="40" height="40" />
                                                <span>Upload Video</span>
                                                <small className="text-gray-500 text-xs">Maximum file size: 150MB</small>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="px-5 py-3.5 border-t border-gray-100 flex justify-end gap-2">
                            {isUploading && (
                                <div className="flex-1 flex items-center gap-3">
                                    <div className="flex-1 bg-gray-200 rounded-full h-2 overflow-hidden">
                                        <div 
                                            className="bg-primary h-full transition-all duration-300"
                                            style={{ width: `${uploadProgress}%` }}
                                        />
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">
                                        {isProcessing ? 'Processing...' : `${uploadProgress}%`}
                                    </span>
                                </div>
                            )}
                            <button 
                                type="button" 
                                className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-3.5 rounded-lg text-sm"
                                onClick={() => setShowEditModal(false)}
                                disabled={isUploading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="button" 
                                className="bg-primary hover:bg-primary/90 text-white font-medium py-2 px-3.5 rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                onClick={handleSaveChanges}
                                disabled={isUploading}
                            >
                                {isProcessing ? 'Processing video...' : isUploading ? 'Uploading...' : 'Save Changes'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default CourseVideo;
